import { useEffect, useState, useCallback } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Search, X } from "lucide-react";
import { DatePicker } from "@/components/DatePicker";

const ACTION_STYLES = {
  create: "bg-emerald-100 text-emerald-800 border-emerald-200",
  update: "bg-amber-100 text-amber-800 border-amber-200",
  delete: "bg-red-100 text-red-800 border-red-200",
};
const ACTION_LABEL = { create: "Ekleme", update: "Güncelleme", delete: "Silme" };
const MODULES = ["Makine", "Bakım", "Stok", "Ekipman", "Kullanıcı"];

export default function Audit() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [module, setModule] = useState("all");
  const [action, setAction] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (module !== "all") params.module = module;
      if (action !== "all") params.action = action;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (q.trim()) params.q = q.trim();
      const res = await api.get("/audit", { params });
      setRows(res.data);
    } finally {
      setLoading(false);
    }
  }, [module, action, dateFrom, dateTo, q]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const clearFilters = () => {
    setModule("all"); setAction("all"); setDateFrom(""); setDateTo(""); setQ("");
  };

  const fmt = (iso) => {
    try { return new Date(iso).toLocaleString("tr-TR"); } catch { return iso; }
  };

  const hasFilters = module !== "all" || action !== "all" || dateFrom || dateTo || q;

  return (
    <Layout title="İşlem Kaydı" subtitle="Kim, neyi, ne zaman ekledi / güncelledi / sildi — denetim günlüğü">
      <Card className="rounded-sm border border-border p-4 bg-white mb-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
          <div className="space-y-1.5 lg:col-span-1">
            <Label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Modül</Label>
            <Select value={module} onValueChange={setModule}>
              <SelectTrigger data-testid="audit-filter-module" className="rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                {MODULES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">İşlem</Label>
            <Select value={action} onValueChange={setAction}>
              <SelectTrigger data-testid="audit-filter-action" className="rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                <SelectItem value="create">Ekleme</SelectItem>
                <SelectItem value="update">Güncelleme</SelectItem>
                <SelectItem value="delete">Silme</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Başlangıç</Label>
            <DatePicker testid="audit-filter-from" value={dateFrom} onChange={setDateFrom} clearable placeholder="Başlangıç" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Bitiş</Label>
            <DatePicker testid="audit-filter-to" value={dateTo} onChange={setDateTo} clearable placeholder="Bitiş" />
          </div>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input data-testid="audit-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ara..." className="pl-9 rounded-sm" />
            </div>
            {hasFilters && (
              <Button data-testid="audit-clear" variant="outline" size="icon" className="rounded-sm shrink-0" onClick={clearFilters}><X className="w-4 h-4" /></Button>
            )}
          </div>
        </div>
      </Card>
      <Card className="rounded-sm border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-zinc-50">
              <TableHead className="text-xs font-bold uppercase tracking-wide">Tarih / Saat</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-wide">Kullanıcı</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-wide">İşlem</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-wide">Modül</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-wide">Kayıt</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-wide">Detay</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground">Yükleniyor...</TableCell></TableRow>}
            {!loading && rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground">Kayıt bulunamadı</TableCell></TableRow>}
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`audit-row-${r.id}`} className="odd:bg-white even:bg-zinc-50/50">
                <TableCell className="font-mono text-sm whitespace-nowrap">{fmt(r.timestamp)}</TableCell>
                <TableCell>
                  <div className="font-medium text-sm">{r.actor_name || "-"}</div>
                  <div className="text-xs text-muted-foreground">{r.actor_email}</div>
                </TableCell>
                <TableCell><Badge variant="outline" className={`rounded-sm text-xs font-bold ${ACTION_STYLES[r.action] || ""}`}>{ACTION_LABEL[r.action] || r.action}</Badge></TableCell>
                <TableCell className="font-medium">{r.module}</TableCell>
                <TableCell>{r.entity}</TableCell>
                <TableCell className="text-muted-foreground text-sm">{r.detail || "-"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </Layout>
  );
}
